import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Sparkles, ChevronRight, ChevronLeft, Check, X, Wand2,
  Target, DollarSign, Clock, Star, ArrowRight, Loader2,
  Calendar, MessageCircle, TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { starsCelebration } from "@/utils/confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Animated match score ring
const MatchRing = ({ score, delay = 0 }) => {
  const [animated, setAnimated] = useState(0);
  const circumference = 2 * Math.PI * 28;

  useEffect(() => {
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setAnimated(prev => {
          if (prev >= score) { clearInterval(interval); return score; }
          return prev + 1;
        });
      }, 12);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timer);
  }, [score, delay]);

  const getColor = (s) => {
    if (s >= 90) return "#22c55e";
    if (s >= 80) return "#eab308";
    return "#f97316";
  };

  return (
    <div className="relative w-16 h-16 flex-shrink-0">
      <svg width="64" height="64" className="transform -rotate-90">
        <circle cx="32" cy="32" r="28" stroke="#e5e7eb" strokeWidth="4" fill="none" />
        <motion.circle
          cx="32" cy="32" r="28"
          stroke={getColor(score)}
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - (animated / 100) * circumference }}
          transition={{ duration: 1.2, delay: delay / 1000, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold text-gray-800">{animated}%</span>
      </div>
    </div>
  );
};

const TreatmentQuiz = ({ isOpen, onClose, scrollToSection }) => {
  const { language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [personalizedMessage, setPersonalizedMessage] = useState("");
  const [aiError, setAiError] = useState(false);

  useEffect(() => {
    if (showResults && aiRecommendations.length > 0) {
      starsCelebration();
    }
  }, [showResults, aiRecommendations]);

  const translations = {
    en: {
      title: "Find Your Perfect Treatment",
      subtitle: "Answer a few questions and our AI will create your personalized plan",
      step: "Step",
      of: "of",
      next: "Next",
      back: "Back",
      seeResults: "Get My AI Recommendations",
      yourRecommendations: "Your Personalized AI Recommendations",
      basedOnAnswers: "Our AI analyzed your profile to find your perfect treatments:",
      perfectFor: "Perfect for",
      bookNow: "Book This Treatment",
      startOver: "Start Over",
      topPick: "TOP PICK FOR YOU",
      alsoRecommend: "Also Recommended",
      matchScore: "Match",
      whyForYou: "Why this is right for you",
      timeline: "Results Timeline",
      maintenance: "Maintenance",
      askAI: "Ask AI About This",
      analyzing: "Our AI is analyzing your profile...",
      analyzingSteps: ["Understanding your concerns...", "Matching treatments to your goals...", "Calculating compatibility scores...", "Generating personalized plan..."],
      errorTitle: "Couldn't generate AI recommendations",
      errorDesc: "Please try again or book a free consultation for personalized advice.",
      tryAgain: "Try Again",
      bookConsult: "Book Free Consultation",
      questions: [
        {
          id: "concern",
          question: "What's your primary concern?",
          icon: Target,
          options: [
            { id: "wrinkles", label: "Fine lines & wrinkles", emoji: "✨" },
            { id: "volume", label: "Loss of volume", emoji: "💫" },
            { id: "lips", label: "Lip enhancement", emoji: "💋" },
            { id: "skin", label: "Skin texture & quality", emoji: "🌟" },
            { id: "contour", label: "Facial contouring", emoji: "💎" },
            { id: "prevention", label: "Prevention & maintenance", emoji: "🛡️" }
          ]
        },
        {
          id: "area",
          question: "Which area bothers you most?",
          icon: Wand2,
          options: [
            { id: "forehead", label: "Forehead & brow", emoji: "😊" },
            { id: "eyes", label: "Around the eyes", emoji: "👁️" },
            { id: "cheeks", label: "Cheeks & midface", emoji: "😌" },
            { id: "lips_area", label: "Lips & mouth area", emoji: "👄" },
            { id: "jawline", label: "Jawline & chin", emoji: "💪" },
            { id: "neck", label: "Neck & decolletage", emoji: "✨" }
          ]
        },
        {
          id: "goal",
          question: "What's your treatment goal?",
          icon: Star,
          options: [
            { id: "subtle", label: "Subtle, natural enhancement", emoji: "🌿" },
            { id: "noticeable", label: "Noticeable but natural", emoji: "⭐" },
            { id: "dramatic", label: "More dramatic results", emoji: "💫" },
            { id: "maintain", label: "Maintain current results", emoji: "🔄" }
          ]
        },
        {
          id: "experience",
          question: "Have you had treatments before?",
          icon: Clock,
          options: [
            { id: "first", label: "This would be my first time", emoji: "🆕" },
            { id: "some", label: "I've had a few treatments", emoji: "📈" },
            { id: "regular", label: "I'm a regular client", emoji: "⭐" }
          ]
        },
        {
          id: "budget",
          question: "What's your budget range?",
          icon: DollarSign,
          options: [
            { id: "starter", label: "Under $300", emoji: "💵" },
            { id: "moderate", label: "$300 - $600", emoji: "💰" },
            { id: "premium", label: "$600 - $1,000", emoji: "💎" },
            { id: "luxury", label: "$1,000+", emoji: "👑" }
          ]
        }
      ]
    },
    fr: {
      title: "Trouvez Votre Traitement Ideal",
      subtitle: "Repondez a quelques questions et notre IA creera votre plan personnalise",
      step: "Etape",
      of: "de",
      next: "Suivant",
      back: "Retour",
      seeResults: "Obtenir Mes Recommandations IA",
      yourRecommendations: "Vos Recommandations IA Personnalisees",
      basedOnAnswers: "Notre IA a analyse votre profil pour trouver vos traitements ideaux:",
      perfectFor: "Parfait pour",
      bookNow: "Reserver Ce Traitement",
      startOver: "Recommencer",
      topPick: "CHOIX #1 POUR VOUS",
      alsoRecommend: "Aussi Recommande",
      matchScore: "Compatibilite",
      whyForYou: "Pourquoi c'est ideal pour vous",
      timeline: "Chronologie des Resultats",
      maintenance: "Entretien",
      askAI: "Demander a l'IA",
      analyzing: "Notre IA analyse votre profil...",
      analyzingSteps: ["Comprehension de vos preoccupations...", "Correspondance des traitements...", "Calcul des scores de compatibilite...", "Generation du plan personnalise..."],
      errorTitle: "Impossible de generer les recommandations IA",
      errorDesc: "Veuillez reessayer ou reservez une consultation gratuite.",
      tryAgain: "Reessayer",
      bookConsult: "Consultation Gratuite",
      questions: [
        {
          id: "concern",
          question: "Quelle est votre preoccupation principale?",
          icon: Target,
          options: [
            { id: "wrinkles", label: "Ridules et rides", emoji: "✨" },
            { id: "volume", label: "Perte de volume", emoji: "💫" },
            { id: "lips", label: "Augmentation des levres", emoji: "💋" },
            { id: "skin", label: "Texture et qualite de la peau", emoji: "🌟" },
            { id: "contour", label: "Contour du visage", emoji: "💎" },
            { id: "prevention", label: "Prevention et maintien", emoji: "🛡️" }
          ]
        },
        {
          id: "area",
          question: "Quelle zone vous preoccupe le plus?",
          icon: Wand2,
          options: [
            { id: "forehead", label: "Front et sourcils", emoji: "😊" },
            { id: "eyes", label: "Contour des yeux", emoji: "👁️" },
            { id: "cheeks", label: "Joues et milieu du visage", emoji: "😌" },
            { id: "lips_area", label: "Levres et bouche", emoji: "👄" },
            { id: "jawline", label: "Machoire et menton", emoji: "💪" },
            { id: "neck", label: "Cou et decollete", emoji: "✨" }
          ]
        },
        {
          id: "goal",
          question: "Quel est votre objectif?",
          icon: Star,
          options: [
            { id: "subtle", label: "Amelioration subtile et naturelle", emoji: "🌿" },
            { id: "noticeable", label: "Visible mais naturel", emoji: "⭐" },
            { id: "dramatic", label: "Resultats plus dramatiques", emoji: "💫" },
            { id: "maintain", label: "Maintenir les resultats actuels", emoji: "🔄" }
          ]
        },
        {
          id: "experience",
          question: "Avez-vous deja eu des traitements?",
          icon: Clock,
          options: [
            { id: "first", label: "Ce serait ma premiere fois", emoji: "🆕" },
            { id: "some", label: "J'ai eu quelques traitements", emoji: "📈" },
            { id: "regular", label: "Je suis un(e) client(e) regulier(e)", emoji: "⭐" }
          ]
        },
        {
          id: "budget",
          question: "Quel est votre budget?",
          icon: DollarSign,
          options: [
            { id: "starter", label: "Moins de 300$", emoji: "💵" },
            { id: "moderate", label: "300$ - 600$", emoji: "💰" },
            { id: "premium", label: "600$ - 1 000$", emoji: "💎" },
            { id: "luxury", label: "1 000$+", emoji: "👑" }
          ]
        }
      ]
    }
  };

  const t = translations[language];
  const questions = t.questions;

  // Fetch AI recommendations
  const fetchAIRecommendations = async () => {
    setIsLoadingAI(true);
    setAiError(false);
    try {
      const response = await fetch(`${BACKEND_URL}/api/quiz/recommendations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...answers, language })
      });
      const data = await response.json();
      if (data.success && data.recommendations?.length > 0) {
        setAiRecommendations(data.recommendations);
        setPersonalizedMessage(data.personalized_message || "");
        setShowResults(true);
      } else {
        setAiError(true);
      }
    } catch (err) {
      console.error("Quiz AI error:", err);
      setAiError(true);
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleAnswer = (questionId, optionId) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      fetchAIRecommendations();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleStartOver = () => {
    setAnswers({});
    setCurrentStep(0);
    setShowResults(false);
    setAiRecommendations([]);
    setPersonalizedMessage("");
    setAiError(false);
    setIsLoadingAI(false);
  };

  const handleBookTreatment = () => {
    onClose();
    setTimeout(() => {
      const bookBtn = document.querySelector('[data-testid="nav-book-now"]');
      if (bookBtn) bookBtn.click();
    }, 100);
  };

  const handleAskAI = (treatmentName) => {
    const msg = language === "fr"
      ? `Je viens de faire le quiz traitement et on m'a recommande "${treatmentName}". Pouvez-vous m'en dire plus sur ce traitement, les resultats attendus, et si c'est adapte pour moi?`
      : `I just took the treatment quiz and was recommended "${treatmentName}". Can you tell me more about this treatment, expected results, and if it's right for me?`;
    onClose();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("open-chat-with-context", { detail: { contextMessage: msg } }));
    }, 300);
  };

  if (!isOpen) return null;

  const currentQuestion = questions[currentStep];
  const currentAnswer = answers[currentQuestion?.id];
  const progress = ((currentStep + 1) / questions.length) * 100;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
            </div>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-3 min-w-[44px] min-h-[44px] hover:bg-white/20 rounded-full transition-colors flex items-center justify-center"
              data-testid="quiz-close-button"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 relative">
              <div className="p-3 bg-white/20 rounded-xl animate-pulse-glow">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t.title}
                </h2>
                <p className="text-white/80 text-sm mt-1">{t.subtitle}</p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {!showResults && !isLoadingAI && !aiError && (
            <div className="px-6 pt-4">
              <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                <span>{t.step} {currentStep + 1} {t.of} {questions.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                  className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
                />
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[55vh]">
            <AnimatePresence mode="wait">
              {/* AI Loading State */}
              {isLoadingAI && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-12"
                >
                  <div className="relative w-24 h-24 mb-6">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-0 border-4 border-primary/20 border-t-primary rounded-full"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Sparkles className="w-10 h-10 text-primary" />
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-gray-800 mb-3">{t.analyzing}</p>
                  <div className="space-y-2 text-center">
                    {t.analyzingSteps.map((step, idx) => (
                      <motion.p
                        key={idx}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 1.2 }}
                        className="text-sm text-gray-500"
                      >
                        {step}
                      </motion.p>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Error State */}
              {aiError && !isLoadingAI && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-12"
                >
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <X className="w-8 h-8 text-red-500" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">{t.errorTitle}</h4>
                  <p className="text-sm text-gray-500 mb-6 text-center">{t.errorDesc}</p>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={handleStartOver}>{t.tryAgain}</Button>
                    <Button onClick={handleBookTreatment} className="btn-accent">{t.bookConsult}</Button>
                  </div>
                </motion.div>
              )}

              {/* Questions */}
              {!showResults && !isLoadingAI && !aiError && (
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <currentQuestion.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {currentQuestion.question}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {currentQuestion.options.map((option, index) => (
                      <motion.button
                        key={option.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleAnswer(currentQuestion.id, option.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all duration-300 hover-lift ${
                          currentAnswer === option.id
                            ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                            : "border-gray-200 hover:border-primary/50"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{option.emoji}</span>
                          <span className="font-medium">{option.label}</span>
                          {currentAnswer === option.id && (
                            <Check className="w-5 h-5 text-primary ml-auto" />
                          )}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* AI Results */}
              {showResults && (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4 animate-pulse-glow">
                      <Sparkles className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {t.yourRecommendations}
                    </h3>
                    <p className="text-muted-foreground">{t.basedOnAnswers}</p>
                  </div>

                  {/* Personalized AI Message */}
                  {personalizedMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-1.5 bg-amber-500 rounded-lg flex-shrink-0">
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed italic">
                          "{personalizedMessage}"
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Recommendations */}
                  <div className="space-y-4">
                    {aiRecommendations.map((rec, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.15 }}
                        className={`rounded-2xl border-2 relative overflow-hidden ${
                          rec.top_pick
                            ? "border-primary bg-gradient-to-br from-primary/5 to-accent/5 shadow-lg"
                            : "border-gray-200 bg-white"
                        }`}
                      >
                        {rec.top_pick && (
                          <div className="bg-gradient-to-r from-primary to-primary/80 text-white text-xs font-bold px-4 py-1.5 text-center">
                            {t.topPick}
                          </div>
                        )}

                        <div className="p-5">
                          <div className="flex items-start gap-4">
                            {/* Match Score */}
                            <MatchRing score={rec.match_score || 85} delay={300 + index * 200} />

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <h4 className="font-bold text-lg text-gray-900">{rec.name}</h4>
                                <span className="text-xl font-bold text-primary whitespace-nowrap">{rec.price}</span>
                              </div>

                              {/* Why for you */}
                              {rec.why_for_you && (
                                <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                                  {rec.why_for_you}
                                </p>
                              )}

                              {/* Benefits */}
                              <div className="flex flex-wrap gap-1.5 mb-3">
                                {(rec.benefits || []).map((benefit, i) => (
                                  <span
                                    key={i}
                                    className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
                                  >
                                    <Check className="w-3 h-3" />
                                    {benefit}
                                  </span>
                                ))}
                              </div>

                              {/* Timeline & Maintenance */}
                              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                                {rec.expected_timeline && (
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-3.5 h-3.5 text-amber-500" />
                                    <span>{rec.expected_timeline}</span>
                                  </div>
                                )}
                                {rec.maintenance && (
                                  <div className="flex items-center gap-1">
                                    <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                                    <span>{rec.maintenance}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                            <Button
                              size="sm"
                              onClick={handleBookTreatment}
                              className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                              data-testid={`quiz-book-${index}`}
                            >
                              <Calendar className="w-3.5 h-3.5 mr-1.5" />
                              {t.bookNow}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAskAI(rec.name)}
                              className="border-cyan-300 text-cyan-600 hover:bg-cyan-50"
                              data-testid={`quiz-ask-ai-${index}`}
                            >
                              <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
                              {t.askAI}
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-100 bg-gray-50">
            {!showResults && !isLoadingAI && !aiError ? (
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className="flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t.back}
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!currentAnswer}
                  className="btn-accent btn-shine flex items-center gap-2"
                >
                  {currentStep === questions.length - 1 ? (
                    <>
                      <Sparkles className="w-4 h-4" />
                      {t.seeResults}
                    </>
                  ) : (
                    <>
                      {t.next}
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            ) : showResults ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" onClick={handleStartOver} className="flex-1">
                  {t.startOver}
                </Button>
                <Button
                  onClick={handleBookTreatment}
                  className="flex-1 btn-accent btn-shine btn-magnetic flex items-center justify-center gap-2"
                >
                  {t.bookNow}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            ) : null}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TreatmentQuiz;
